import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { postLoginRefreshManager } from '@/services/PostLoginRefreshManager';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export const usePostLoginRefresh = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      // Update login streak on app load (priority 5 - runs first)
      const updateLoginStreak = async () => {
        console.log('Updating login streak for user...');
        try {
          const { error } = await supabase.rpc('update_user_login_streak', {
            p_user_id: parseInt(user.id)
          });
          if (error) {
            console.error('Error updating login streak:', error);
          } else {
            console.log('Login streak updated successfully');
          }
        } catch (err) {
          console.error('Failed to update login streak:', err);
        }
      };

      const refreshReactQueryData = async () => {
        console.log('Invalidating React Query cache for user-specific data...');
        
        // Invalidate all user-specific queries
        await queryClient.invalidateQueries({ 
          queryKey: ['orders'], 
          exact: false 
        });
        
        await queryClient.invalidateQueries({ 
          queryKey: ['user'], 
          exact: false 
        });

        await queryClient.invalidateQueries({ 
          queryKey: ['wishlist'], 
          exact: false 
        });

        await queryClient.invalidateQueries({ 
          queryKey: ['cart'], 
          exact: false 
        });

        await queryClient.invalidateQueries({ 
          queryKey: ['addresses'], 
          exact: false 
        });

        await queryClient.invalidateQueries({ 
          queryKey: ['wallet'], 
          exact: false 
        });

        await queryClient.invalidateQueries({ 
          queryKey: ['user-streak'], 
          exact: false 
        });

        await queryClient.invalidateQueries({ 
          queryKey: ['task-tracking'], 
          exact: false 
        });

        console.log('React Query cache invalidated successfully');
      };

      // Auto-check all tasks after cache refresh (priority 15)
      const checkAllTasks = async () => {
        console.log('Auto-checking all tasks for user...');
        try {
          const { error } = await supabase.rpc('check_all_user_tasks', {
            p_user_id: parseInt(user.id)
          });
          if (error) {
            console.error('Error checking tasks:', error);
          } else {
            console.log('All tasks checked successfully');
            // Refresh task-related queries after checking
            await queryClient.invalidateQueries({ queryKey: ['task-tracking'] });
            await queryClient.invalidateQueries({ queryKey: ['user-streak'] });
          }
        } catch (err) {
          console.error('Failed to check tasks:', err);
        }
      };

      // Register callbacks with priorities (lower = runs first)
      postLoginRefreshManager.register('login-streak', updateLoginStreak, 5);
      postLoginRefreshManager.register('react-query-cache', refreshReactQueryData, 10);
      postLoginRefreshManager.register('auto-check-tasks', checkAllTasks, 15);
      
      return () => {
        postLoginRefreshManager.unregister('login-streak');
        postLoginRefreshManager.unregister('react-query-cache');
        postLoginRefreshManager.unregister('auto-check-tasks');
      };
    }
  }, [user, queryClient]);
};